import {
  DEBUG_FLAG,
  LOG_BRAND_PREFIX,
  APP_PREFIX,
  PACKAGE_PREFIX,
} from './constants';
import { emitLogRecord, hasLogSinks, type LogLevel } from './log-sinks';

                                                                                                           
export function formatPackageLabel(packageName: string): string {
  return packageName.replace(/^@sync-video\//, '');
}

const PREFIX_PACKAGE = (packageName: string) =>
  `[${formatPackageLabel(packageName)}]` as const;
const PREFIX_SCOPE = (scope: string) => `[${scope}]` as const;

const STYLE_TIMESTAMP = 'color: #3b82f6; font-weight: 500;';
const STYLE_BRAND = 'color: #6366f1; font-weight: 600;';
const STYLE_PACKAGE = 'color: #22d3ee; font-weight: 500;';
const STYLE_SCOPE = 'color: #f59e0b; font-weight: 500;';
const STYLE_MSG_INFO = 'color: #86efac;';
const STYLE_MSG_WARN = 'color: #fde047;';
const STYLE_MSG_ERROR = 'color: #f87171;';
const STYLE_MSG_DEBUG = 'color: #a1a1aa;';

const timestampFormatter = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatTimestamp(date: Date): string {
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return timestampFormatter.format(date).replace(', ', ':') + '.' + ms;
}

   
                                                  
                                                                       
   
export class Logger {
  private static enabled = false;
  private static storageListenerAdded = false;
  private static globalMetadata: Record<string, unknown> = {};
  private static registeredAppName: string | null = null;

  constructor(
    private readonly packageName: string,
    private readonly scope: string,
  ) {
    Logger.ensureDebugListener();
  }

                                                                       
  public static registerApp(appName: string): void {
    Logger.registeredAppName = appName;
  }

  private static resolvePackageLabel(packageName: string): string {
    if (!Logger.registeredAppName || !packageName.startsWith(PACKAGE_PREFIX)) {
      return packageName;
    }

    return `${APP_PREFIX}${Logger.registeredAppName}/${packageName}`;
  }

  private static ensureDebugListener(): void {
    if (Logger.storageListenerAdded) {
      return;
    }

    Logger.enabled = Logger.checkDebugFlag();
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', () => {
        Logger.enabled = Logger.checkDebugFlag();
      });
    }
    Logger.storageListenerAdded = true;
  }

  private static checkDebugFlag(): boolean {
    try {
      return localStorage.getItem(DEBUG_FLAG) === 'true';
    } catch {
      return false;
    }
  }

     
                                                  
                                                                                 
     
  public static setDebugEnabled(enabled: boolean): void {
    Logger.ensureDebugListener();
    Logger.enabled = enabled;
  }

                                                                                
  public static setGlobalMetadata(metadata: Record<string, unknown>): void {
    Logger.globalMetadata = { ...metadata };
  }

  private styled(
    fn: (template: string, ...rest: unknown[]) => void,
    msgStyle: string,
    message: string,
    level: LogLevel,
    meta?: unknown,
  ): void {
    const ts = formatTimestamp(new Date());
    const escaped = message.replace(/%/g, '%%');
    const packageLabel = Logger.resolvePackageLabel(this.packageName);
    const template = `%c${ts}%c ${LOG_BRAND_PREFIX}%c${PREFIX_PACKAGE(packageLabel)}%c${PREFIX_SCOPE(this.scope)}%c ${escaped}`;

    const args: [string, ...unknown[]] = [
      template,
      STYLE_TIMESTAMP,
      STYLE_BRAND,
      STYLE_PACKAGE,
      STYLE_SCOPE,
      msgStyle,
    ];

    if (meta !== undefined) {
      args.push(meta);
    }

    if (Object.keys(Logger.globalMetadata).length > 0) {
      args.push(Logger.globalMetadata);
    }

    fn(...args);

       
                              
      
                                                                             
                                                                               
                                                 
       
    if (hasLogSinks()) {
      emitLogRecord({
        time: Date.now(),
        level,
        package: packageLabel,
        scope: this.scope,
        message,
        ...(meta === undefined ? {} : { meta }),
      });
    }
  }

  public log(message: string, meta?: unknown): void {
    if (Logger.enabled) {
      this.styled(
        console.log.bind(console),
        STYLE_MSG_INFO,
        message,
        'log',
        meta,
      );
    }
  }

  public warn(message: string, meta?: unknown): void {
    if (Logger.enabled) {
      this.styled(
        console.warn.bind(console),
        STYLE_MSG_WARN,
        message,
        'warn',
        meta,
      );
    }
  }

  public error(message: string, meta?: unknown): void {
    this.styled(
      console.error.bind(console),
      STYLE_MSG_ERROR,
      message,
      'error',
      meta,
    );
  }

  public debug(message: string, meta?: unknown): void {
    if (Logger.enabled) {
      this.styled(
        console.debug.bind(console),
        STYLE_MSG_DEBUG,
        message,
        'debug',
        meta,
      );
    }
  }
}

   
                                                              
                                                                         
   
export class LoggerFactory {
  private constructor(private readonly packageName: string) {}

                                                                              
  public static registerApp(appName: string): void {
    Logger.registerApp(appName);
  }

                                                                                           
  public static setDebugEnabled(enabled: boolean): void {
    Logger.setDebugEnabled(enabled);
  }

                                                                          
  public static forPackage(packageName: string): LoggerFactory {
    return new LoggerFactory(packageName);
  }

                                                   
  public static forApp(appName: string): LoggerFactory {
    LoggerFactory.registerApp(appName);
    return new LoggerFactory(`${APP_PREFIX}${appName}`);
  }

                                                              
  public static forLib(packageName: string): LoggerFactory {
    return new LoggerFactory(`${PACKAGE_PREFIX}${packageName}`);
  }

                                                    
  public create(scope: string): Logger {
    return new Logger(this.packageName, scope);
  }

  public get package(): string {
    return this.packageName;
  }
}
